import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PopupService } from '../../../shared/modal/popup.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BreedingMethod } from '../../../shared/breeding-method/model/breeding-method';
import { GermplasmProgenitorsContext } from './germplasm-progenitors.context';
import { GermplasmProgenitorsDetails } from '../../../shared/germplasm/model/germplasm.model';
import { GermplasmService } from '../../../shared/germplasm/service/germplasm.service';
import { JhiEventManager } from '../../../shared/core-utils';
import { ParamContext } from '../../../shared/service/param.context';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { BreedingMethodTypeEnum } from '../../../shared/breeding-method/model/breeding-method-type.model';
import { BreedingMethodFilterTypeEnum } from '../../../shared/breeding-methods-select/breeding-methods-select.component';
import { GermplasmDetailsPopupService } from '../../../germplasm-details/germplasm-details-popup.service';
import { GermplasmDetailsContext } from '../../../germplasm-details/germplasm-details.context';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { AlertService } from '../../../shared/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-germplasm-progenitors-modal',
    templateUrl: './germplasm-progenitors-modal.component.html'
})
export class GermplasmProgenitorsModalComponent implements OnInit, OnDestroy {

    private readonly UNKNOWN = '0';

    gid: number;
    progenitorsDetails: GermplasmProgenitorsDetails;
    isLoading: boolean;
    isGenerative: boolean;
    breedingMethodSelectedId: string;
    breedingMethodSelected: BreedingMethod;
    femaleParent: string;
    maleParent: string;

    selectorTarget: string;
    subscriptionManager = new SubscriptionsManager();

    constructor(public activeModal: NgbActiveModal,
                private eventManager: JhiEventManager,
                public translateService: TranslateService,
                private germplasmProgenitorsContext: GermplasmProgenitorsContext,
                private germplasmService: GermplasmService,
                private alertService: AlertService,
                private router: Router,
                private paramContext: ParamContext,
                private modalService: NgbModal) {
        this.progenitorsDetails = this.germplasmProgenitorsContext.germplasmProgenitorsDetails;
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.initializeForm();
        this.registerGermplasmSelectorSelected();
    }

    registerGermplasmSelectorSelected() {
        this.subscriptionManager.add = this.eventManager.subscribe('germplasmSelectorSelected', (event) => {
            if (this.selectorTarget === 'female') {
                this.femaleParent = event.content;
            } else if (this.selectorTarget === 'male') {
                this.maleParent = event.content;
            }
        });
    }

    clear() {
        this.activeModal.dismiss('cancel');
    }

    save() {
        const maleParentsList = this.maleParent.split(',');
        const femaleParentsList = this.femaleParent.split(',');

        if (this.isNumberOutOfRange([this.femaleParent])) {
            this.alertService.error('germplasm-progenitors-modal.validation.gid.out.of.range', { param: this.isMutation() ? 'Origin' : 'Female Parent or Group Source' });
            return;
        }

        if (!this.isMutation() && this.isNumberOutOfRange(maleParentsList)) {
            this.alertService.error('germplasm-progenitors-modal.validation.gid.out.of.range', { param: 'Male Parent or Immediate Source' });
            return;
        }

        if (!this.isMutation() && !this.AllowMultipleMale() && maleParentsList.length > 1) {//
            this.alertService.error('germplasm-progenitors-modal.validation.only.one.male.parent.is.allowed', { param: this.breedingMethodSelected.name });
            return;
        }

        if (!this.isMutation() && !this.AllowMultipleFemale() && femaleParentsList.length > 1) {//
            this.alertService.error('germplasm-progenitors-modal.validation.only.one.female.parent.is.allowed', { param: this.breedingMethodSelected.name });
            return;
        }

        const maleParentsNumbers = maleParentsList.map((item) => Number(item));
        const femaleParentsNumbers = femaleParentsList.map((item) => Number(item));

        if (!this.isGenerative && this.progenitorsDetails.numberOfDerivativeProgeny > 0
            && (this.progenitorsDetails.breedingMethodType === BreedingMethodTypeEnum.GENERATIVE || this.hasProgenitorsChanges())) {
            const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
            confirmModalRef.componentInstance.title = this.translateService.instant('germplasm-progenitors-modal.confirm.update.title');
            confirmModalRef.componentInstance.message = this.translateService.instant('germplasm-progenitors-modal.confirm.update.message', { param: this.progenitorsDetails.numberOfDerivativeProgeny });
            confirmModalRef.result.then(() => {
                this.updateGermplasmProgenitors(maleParentsNumbers, femaleParentsNumbers);
                this.activeModal.close();
            }, () => this.activeModal.dismiss());
        } else {
            this.updateGermplasmProgenitors(maleParentsNumbers, femaleParentsNumbers);
        }
    }

    hasProgenitorsChanges() {
        // Compare the maleParentIds without splitting the string since only one germplasm is allowed as male parent for non-generative germplasm
        return this.getFemaleParentId(this.progenitorsDetails) !== this.femaleParent || this.getMaleParentId(this.progenitorsDetails) !== this.maleParent;
    }

    updateGermplasmProgenitors(maleParentsList, femaleParentsList) {
        this.isLoading = true;
        const firstMaleElement = maleParentsList.shift();
        const firstFemaleElement = femaleParentsList.shift();

        this.germplasmService.updateGermplasmProgenitors(this.gid, {
            breedingMethodId: this.breedingMethodSelected.mid,
            gpid1: !this.isMutation() ? firstFemaleElement : 0,
            gpid2: !this.isMutation() ? firstMaleElement : 0,
            maleProgenitors: !this.isMutation() ? maleParentsList : [],
            femaleProgenitors: !this.isMutation() ? femaleParentsList : []
        }).toPromise().then((result) => {
            this.alertService.success('germplasm-progenitors-modal.edit.success');
            this.notifyChanges();
        }).finally(() => {
            this.isLoading = false;
        });
    }

    isFormValid(f) {
        if (this.isMutation() && !this.femaleParent) {
            return false;
        }
        return f.form.valid && !this.isLoading && this.breedingMethodSelected && this.femaleParent && this.maleParent;
    }

    initializeForm() {
        this.breedingMethodSelectedId = String(this.progenitorsDetails.breedingMethodId);
        this.isGenerative = this.progenitorsDetails.breedingMethodType === BreedingMethodTypeEnum.GENERATIVE;
        this.femaleParent = this.getFemaleParentId(this.progenitorsDetails);
        this.maleParent = this.getMaleParentId(this.progenitorsDetails);
    }

    notifyChanges(): void {
        this.eventManager.broadcast({ name: 'progenitorsChanged' });
        this.clear();
    }

    getFemaleParentId(progenitorsDetails: GermplasmProgenitorsDetails) {
        if (progenitorsDetails.femaleParents && progenitorsDetails.femaleParents.length > 0) {
            return this.progenitorsDetails.femaleParents.map((parent) => parent.gid).join(',');
        } else if (progenitorsDetails.groupSource) {
            return String(this.progenitorsDetails.groupSource.gid);
        }
        // Return 0 if unknown
        return this.UNKNOWN;
    }

    getMaleParentId(progenitorsDetails: GermplasmProgenitorsDetails) {
        if (progenitorsDetails.maleParents && progenitorsDetails.maleParents.length > 0) {
            return this.progenitorsDetails.maleParents.map((parent) => parent.gid).join(',');
        } else if (progenitorsDetails.immediateSource) {
            return String(this.progenitorsDetails.immediateSource.gid);
        }
        // Return 0 if unknown
        return this.UNKNOWN;
    }

    isMutation(): boolean {
        return this.breedingMethodSelected && this.breedingMethodSelected.numberOfProgenitors === 1;
    }

    AllowMultipleFemale(): boolean {
        return this.breedingMethodSelected && this.breedingMethodSelected.type === BreedingMethodTypeEnum.GENERATIVE && this.breedingMethodSelected.numberOfFemaleParents === 0;
    }
    AllowMultipleMale(): boolean {
        return this.breedingMethodSelected && this.breedingMethodSelected.type === BreedingMethodTypeEnum.GENERATIVE && this.breedingMethodSelected.numberOfProgenitors === 0;
    }

    openGermplasmSelector(selectMultiple: boolean, target: string): void {
        this.selectorTarget = target;
        this.router.navigate(['/', { outlets: { popup: 'germplasm-selector-dialog' } }], {
            queryParamsHandling: 'merge',
            queryParams: {
                cropName: this.paramContext.cropName,
                loggedInUserId: this.paramContext.loggedInUserId,
                programUUID: this.paramContext.programUUID,
                selectMultiple
            }
        });
    }

    isNumberOutOfRange(numbersString: string[]) {
        const maxInteger = 2147483647; // Maxiumum 32 bit integer;
        return numbersString.some((num) => Number.isNaN(Number.parseInt(num, 10)) || Number(num) > maxInteger);
    }

    onMethodChanged(selectedBreedingMethod: BreedingMethod): void {
        if (selectedBreedingMethod) {
            this.breedingMethodSelectedId = String(selectedBreedingMethod.mid);
            this.breedingMethodSelected = selectedBreedingMethod;
        }
    }

    breedingMethodOptionChanged(methodType: BreedingMethodFilterTypeEnum) {
        this.isGenerative = methodType === BreedingMethodFilterTypeEnum.GENERATIVE_ONLY;
        this.breedingMethodSelectedId = null;
        this.breedingMethodSelected = null;
    }

}

@Component({
    selector: 'jhi-germplasm-progenitors-popup',
    template: ''
})
export class GermplasmProgenitorsPopupComponent implements OnInit {
    constructor(private route: ActivatedRoute,
                private popupService: PopupService,
                private germplasmDetailsPopupService: GermplasmDetailsPopupService,
                private germplasmDetailsContext: GermplasmDetailsContext) {
    }

    ngOnInit(): void {
        const gid = Number(this.route.snapshot.paramMap.get('gid'));
        if (this.germplasmDetailsContext.openedFromWorkbench) {
            this.germplasmDetailsPopupService.close();
        }
        const modal = this.popupService.open(GermplasmProgenitorsModalComponent as Component, { modalDialogClass: 'modal-md', backdrop: 'static' });
        modal.then((modalRef) => {
            modalRef.componentInstance.gid = gid;
            if (this.germplasmDetailsContext.openedFromWorkbench) {
                modalRef.result.then((result) => {
                    this.germplasmDetailsPopupService.open(gid, 'pedigree');
                }, () => {
                    this.germplasmDetailsPopupService.open(gid, 'pedigree');
                });
            }
        });
    }

}
